/**
 * Unit-identity values on a registration (holding EAV).
 *
 * Which attributes count as identity is tenant config, never a hardcoded
 * code name. Callers pass the codes from the holding entity
 * (``metadata.identity_attributes``, ``semantic_role: unit_identity``,
 * or value-stock-backed attributes). Empty codes means this tenant has
 * no unit identity field - callers must not render a row.
 */

type AttributeLike = {
  code_name?: string
  display_name?: string
  value?: unknown
  semantic_role?: string
  identity?: boolean
}

export type HoldingIdentityField = {
  value: string
  label: string
  codeName: string
}

function textFromValue(value: unknown): string {
  if (value == null || value === '') return ''
  if (typeof value === 'string' || typeof value === 'number') {
    return String(value).trim()
  }
  if (typeof value === 'object') {
    const rec = value as Record<string, unknown>
    if (typeof rec.display === 'string' && rec.display.trim()) {
      return rec.display.trim()
    }
    if (rec.value != null && rec.value !== value) {
      return textFromValue(rec.value)
    }
  }
  return ''
}

function codesMarkedOnValues(
  attrs: AttributeLike[] | Record<string, unknown> | null | undefined,
): string[] {
  if (!Array.isArray(attrs)) return []
  return attrs
    .filter(
      (attr) =>
        Boolean(attr?.code_name) &&
        (attr.identity === true || attr.semantic_role === 'unit_identity'),
    )
    .map((attr) => attr.code_name as string)
}

function readIdentity(
  attrs: AttributeLike[] | Record<string, unknown> | null | undefined,
  codes: readonly string[],
): HoldingIdentityField | null {
  if (!attrs || codes.length === 0) return null
  if (Array.isArray(attrs)) {
    const byCode = new Map(
      attrs
        .filter((attr) => typeof attr?.code_name === 'string')
        .map((attr) => [attr.code_name as string, attr]),
    )
    for (const code of codes) {
      const hit = byCode.get(code)
      const value = textFromValue(hit?.value)
      if (!value) continue
      return {
        value,
        label: (hit?.display_name || '').trim(),
        codeName: code,
      }
    }
    return null
  }
  for (const code of codes) {
    const value = textFromValue(attrs[code])
    if (!value) continue
    return { value, label: '', codeName: code }
  }
  return null
}

export function holdingIdentityField(
  holding: {
    attribute_values?: AttributeLike[] | Record<string, unknown> | null
    product?: { attribute_values?: AttributeLike[] | null } | null
  },
  codes: readonly string[] = [],
): HoldingIdentityField | null {
  const resolved = [
    ...new Set([
      ...codes,
      ...codesMarkedOnValues(holding.attribute_values),
      ...codesMarkedOnValues(holding.product?.attribute_values),
    ]),
  ]
  return (
    readIdentity(holding.attribute_values, resolved) ||
    readIdentity(holding.product?.attribute_values, resolved)
  )
}

/** Value-only helper for search filters and tests. */
export function holdingSerialNumber(
  holding: {
    attribute_values?: AttributeLike[] | Record<string, unknown> | null
    product?: { attribute_values?: AttributeLike[] | null } | null
  },
  codes: readonly string[] = [],
): string {
  return holdingIdentityField(holding, codes)?.value ?? ''
}
